/**
 * EmailThreadOverviewEnrichment — Enriched overview rows for email threads.
 *
 * Renders derived data:
 * - Visibility (from `object.access_summary.privacy_state`)
 * - Source integration (the `ahoy_email_thread_to_integration` target as a
 *   clickable EntityBadge, resolved from the store or associated_objects fallback)
 *
 * OverviewSection filters out `ahoy_email_thread_to_integration` from its
 * auto-rendered association rows to avoid duplicating the Source row here.
 */

import { Link03, Lock01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useCrmObjectLive } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { formatIntegrationLabel } from '@/lib/integrations/formatIntegrationLabel';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { OverviewFieldRow } from '../components/OverviewFieldRow';

// ============================================================================
// Main component
// ============================================================================

interface EmailThreadOverviewEnrichmentProps {
  object: CrmObject;
}

function formatVisibility(visibility: 'private' | 'org'): string {
  return visibility === 'private' ? 'Private' : 'Visible to org';
}

/**
 * Resolve the integration CrmObject via the canonical store-first-fetch-if-missing
 * hook, with `associated_objects` as an instant-first-paint fallback only.
 *
 * The integration edge is excluded from the overview's own hydration and the
 * AssociationsTab is lazy-mounted, so nothing else hydrates it on first paint.
 * `useCrmObjectLive` covers the slideout path (store + fetch); the
 * `associated_objects` map covers the full-page record GET path.
 */
function useIntegrationObject(
  object: CrmObject,
  integrationEntityId: string | undefined
): CrmObject | undefined {
  const storeEntity = useCrmObjectLive(integrationEntityId, object.org_id);

  return useMemo(() => {
    if (!integrationEntityId) {
      return undefined;
    }
    if (storeEntity) {
      return storeEntity;
    }
    // Fallback: full-page record GET ships an `associated_objects` map that
    // isn't reflected in the store on first paint.
    return object.associated_objects?.[integrationEntityId];
  }, [storeEntity, object, integrationEntityId]);
}

export function EmailThreadOverviewEnrichment({
  object,
}: EmailThreadOverviewEnrichmentProps) {
  const integrationEntityId = useMemo(() => {
    if (object.entity_type_name !== 'ahoy_email_thread') {
      return undefined;
    }
    return object.associations?.find(
      a => a.association_type_name === 'ahoy_email_thread_to_integration'
    )?.target_entity_id;
  }, [object.entity_type_name, object.associations]);

  const integrationObject = useIntegrationObject(object, integrationEntityId);

  const visibility = useMemo<'private' | 'org' | undefined>(() => {
    const raw = object.access_summary?.privacy_state;
    if (raw === 'org' || raw === 'private') {
      return raw;
    }
    return undefined;
  }, [object.access_summary]);

  const openSlideout = useOpenSlideoutOptional();

  const handleIntegrationClick = useCallback(() => {
    if (integrationObject) {
      openSlideout?.(integrationObject.entity_id);
    }
  }, [integrationObject, openSlideout]);

  const integrationBadgeState = useMemo(() => {
    if (!integrationObject) {
      return null;
    }
    const baseName = getDisplayName(integrationObject);
    const connectionStatus = getPropertyValue<string>(
      integrationObject.properties?.ahoy_connection_status
    );
    const displayName = formatIntegrationLabel({
      name: baseName,
      connectionStatus,
    });
    return {
      kind: 'data' as const,
      data: {
        id: integrationObject.entity_id,
        entityType: integrationObject.entity_type_name,
        displayName,
      },
    };
  }, [integrationObject]);

  if (visibility === undefined && !integrationObject) {
    return null;
  }

  return (
    <>
      {visibility !== undefined && (
        <OverviewFieldRow
          icon={Lock01}
          label='Visibility'
          valueClassName='text-xs md:text-sm font-normal text-fg-primary'
        >
          {formatVisibility(visibility)}
        </OverviewFieldRow>
      )}
      {integrationBadgeState && (
        <OverviewFieldRow icon={Link03} label='Source'>
          <EntityBadge
            state={integrationBadgeState}
            variant='xs'
            onClick={openSlideout ? handleIntegrationClick : undefined}
          />
        </OverviewFieldRow>
      )}
    </>
  );
}
